import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Card, PageHeader, Button, showSnackbar } from "@cofano/cck";
import { Box, Grid2 as Grid, Tab, Tabs } from "@mui/material";
import Info from "@mui/icons-material/Info";

import useTabs from "components/hooks/useTabs";
import Helmet from "components/helmet/Helmet";
import ExternalMasterdataAlert, { ExternalMasterdataWarningIcon, MasterdataEntity } from "components/masterdata/ExternalMasterdataAlert";

import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language } from "imports/Utils";

import ShipDetailsForm from "./components/form/ShipDetailsForm";
import ShipGeneralForm from "./components/form/ShipGeneralForm";
import ShipHoldsForm from "./components/form/ShipHoldsForm";
import ShipContactsForm from "./components/form/ShipContactsForm";
import ShipAvailabilityForm from "./components/form/ShipAvailabilityForm";
import ShipTonnageCertificatesForm from "./components/form/ShipTonnageCertificatesForm";
import ShipAgentsForm from "./components/form/ShipAgentsForm";
import ShipTanksForm from "./components/form/ShipTanksForm";
import CreateShipHoldDialog from "./components/dialogs/CreateShipHoldDialog";
import SelectBayPlanTemplateDialog from "./components/dialogs/SelectBayPlanTemplateDialog";
import CreateBayPlanTemplateDialog from "./components/dialogs/CreateBayPlanTemplateDialog";

import { ShipTabs } from "./utils/ShipUtils";
import { shipSchema, ShipSchemaInput, ShipSchemaOutput } from "./utils/ship.schema";

import { AvailabilityEnum, BargeType, CharterSubtype, TankConfiguration, VesselType } from "__generated__/enums";
import { InsertOrUpdateShipDocument } from "__generated__/view/ships/graphql/ship.mutations.generated";
import { GetShipForFormDocument } from "__generated__/view/ships/graphql/ship.queries.generated";

const ShipCreateEdit = () => {

	const navigate = useNavigate();
	const location = useLocation();
	const { id } = useParams();

	const session = useStoreState(store => store.session);
	const stateTab = location.state?.tab;

	const isShipDefaultAlwaysAvailable = hasPluginPermission(session, "shipDefaultAlwaysAvailable");

	const [ shipHoldDialogOpen, openShipHoldDialog ] = useState(false);
	const [ selectBayPlanTemplateDialogOpen, openSelectBayPlanTemplateDialog ] = useState(false);
	const [ createBayPlanTemplateDialogOpen, openCreateBayPlanTemplateDialog ] = useState(false);

	const defaultValues: ShipSchemaInput = {
		id: null,
		externalId: "",
		name: "",
		vesselType: VesselType.Barge,
		abbreviation: "",
		isDummy: false,
		imo: null,
		eni: "",
		mmsi: null,
		licensePlate: "",
		gmp: "",
		niwo: "",
		cemtClassificationType: null,
		yearOfBuild: null,
		flag: "NL",
		bargeType: null,
		classificationSociety: "",
		insurer: "",
		adnProperties: {
			shipType: null,
			cargoTankDesign: null,
			cargoTankType: null,
		},
		tankCladdingType: null,
		capacityTon: null,
		capacityM3: null,
		capacityTeu: null,
		tracks: null,
		lengthCm: null,
		widthCm: null,
		minDraughtCm: null,
		maxDraughtCm: null,
		pumpCapacity: null,
		numberOfTanks: null,
		fuelType: null,
		avgVelocityEmptyKmH: null,
		avgVelocityFullKmH: null,
		avgFuelConsumptionLtrH: null,
		avgFuelConsumptionLtrKm: null,
		yearLastMaintenance: null,
		bobbyCat: false,
		sailors: null,
		numReeferConnections: null,
		dropline: null,
		trussing: "",
		hasPushBow: false,
		hasPushingCertificate: false,
		cellGuides: false,
		hasShutters: false,
		shuttersType: "",
		deckLoad: null,
		avgSpeedUpstreamKmH: null,
		avgSpeedDownstreamKmH: null,
		numberOfCranes: null,
		maxCranesOnQuay: null,
		trackerId: null,
		trackTraceProvider: null,
		maxTonBallast: null,
		airDraft: null,
		corridor: "",
		tankEye: false,
		hasHeatingInstallation: false,
		boiler: false,
		spillRail: false,
		selfPropelled: false,
		sailingSchedule: null,
		memo: "",
		planningNotes: "",

		shipManager: null,
		skipper: null,
		shipOwner: null,
		operator: null,
		coupledShip: null,
		defaultCharterSubtype: hasOptionalFieldPermission(session, Fields.SHIP, "defaultCharterType") ? CharterSubtype.Spot : null,
		retrieveActuals: false,
		showLastProducts: false,
		tracking: false,
		advancedTracking: false,

		bayPlan: null,

		tankProperties: {
			tankConfiguration: TankConfiguration.CenterTanks,
		},
		tanks: [],

		contacts: [],

		availability: {
			availableMonday: isShipDefaultAlwaysAvailable,
			availableTuesday: isShipDefaultAlwaysAvailable,
			availableWednesday: isShipDefaultAlwaysAvailable,
			availableThursday: isShipDefaultAlwaysAvailable,
			availableFriday: isShipDefaultAlwaysAvailable,
			availableSaturday: isShipDefaultAlwaysAvailable,
			availableSunday: isShipDefaultAlwaysAvailable,

			availableHoursADay: null,
			availableDayNight: AvailabilityEnum.Day,
			defaultAvailability: isShipDefaultAlwaysAvailable,
		},

		workingHours: {
			id: null,
			mondayFrom:	null,
			mondayTo: null,
			tuesdayFrom: null,
			tuesdayTo: null,
			wednesdayFrom: null,
			wednesdayTo: null,
			thursdayFrom: null,
			thursdayTo: null,
			fridayFrom: null,
			fridayTo: null,
			saturdayFrom: null,
			saturdayTo: null,
			sundayFrom: null,
			sundayTo: null,
		},

		tonnageCertificates: defaultTonnageCertificates,
		agents: [],
	};

	const form = useForm<ShipSchemaInput, unknown, ShipSchemaOutput>({
		resolver: zodResolver(shipSchema(session)),
		reValidateMode: "onChange",
		mode: "onChange",
		defaultValues: defaultValues,
	});

	const { handleSubmit, reset, watch, setValue } = form;

	const [ saveShip, { loading } ] = useMutation(InsertOrUpdateShipDocument);

	const { selectedTab, selectTab } = useTabs<ShipTabs>({
		defaultSelected: stateTab ? (stateTab === ShipTabs.Documents ? ShipTabs.General : stateTab) : ShipTabs.General,
	});

	const save = (data: ShipSchemaOutput) => {

		const { skipper, shipOwner: _shipOwner, ...input } = data;

		saveShip({
			variables: {
				input: {
					...input,
					id: input.id ?? undefined,
					abbreviation: input.abbreviation || undefined,
					cemtClassificationType: input.cemtClassificationType ? { id: input.cemtClassificationType.id } : undefined,
					externalId: data.externalId ? data.externalId.trim() : undefined,
					lengthCm: data.lengthCm ? data.lengthCm * 100 : null,
					widthCm: data.widthCm ? data.widthCm * 100 : null,
					minDraughtCm: data.minDraughtCm ? data.minDraughtCm * 100 : null,
					maxDraughtCm: data.maxDraughtCm ? data.maxDraughtCm * 100 : null,
					shipManager: input.shipManager ? { id: input.shipManager.id } : undefined,
					shipOwnerId: _shipOwner?.id,
					operator: input.operator ? { id: input.operator.id } : undefined,
					coupledShip: input.coupledShip ? { id: input.coupledShip.id } : undefined,
					defaultCharterSubtype: hasOptionalFieldPermission(session, Fields.SHIP, "defaultCharterType") ? input.defaultCharterSubtype : null,
					retrieveActuals: input.retrieveActuals,
					showLastProducts: input.showLastProducts,
					tracking: input.tracking,
					advancedTracking: input.advancedTracking,
					tankProperties: input.bargeType === BargeType.Tanker ? input.tankProperties : undefined,
					tanks: input.bargeType === BargeType.Tanker ? input.tanks : [],
					tonnageCertificates: input.tonnageCertificates.map(it => ({ ...it, tonnage: it.tonnage ?? undefined })),
					agents: input.agents.map(agent => ({
						agent: { id: agent.agent.id },
						location: { id: agent.location.id },
					})),
					bayPlan: input.bayPlan ? JSON.stringify(input.bayPlan) : undefined,
				},
			},
		}).then((response) => {
			if (!skipper && response.data?.insertOrUpdateShip.skipper) {
				navigate(`/view/relations/${response.data.insertOrUpdateShip.skipper.id}/details`, { state: { tab: "skipperShips" } });
				showSnackbar(intl.formatMessage({ id: "SHIP_ADD_NEW_RELATION_HAS_BEEN_CREATED" }), { variant: "warning" });
			} else {
				navigate(`/view/ships/${response.data?.insertOrUpdateShip.id}/details`, { state: { tab: selectedTab } });
			}
		});
	};

	const hasErrorInContactsTab = () => {
		return !!form.formState.errors?.contacts;
	};

	const hasErrorInAgentsTab = () => {
		return !!form.formState.errors?.agents;
	};

	const [ getShip, { loading: shipLoading, data: { ship: currentShip } = {} } ] = useLazyQuery(GetShipForFormDocument);

	useEffect(() => {
		if (id) {
			getShip({
				variables: {
					id: Number(id),
				},
			}).then(response => {
				reset({
					id: response.data?.ship.id ?? null,
					externalId: response.data?.ship?.externalId ?? "",
					name: response.data?.ship.name ?? "",
					vesselType: response.data?.ship.vesselType ?? VesselType.Barge,
					abbreviation: response.data?.ship.abbreviation ?? "",
					isDummy: response.data?.ship.isDummy ?? false,
					imo: response.data?.ship.imo ?? null,
					eni: response.data?.ship.eni ?? "",
					mmsi: response.data?.ship.mmsi ?? null,
					licensePlate: response.data?.ship.licensePlate ?? "",
					gmp: response.data?.ship.gmp ?? "",
					niwo: response.data?.ship.niwo ?? "",
					cemtClassificationType: response.data?.ship.cemtClassificationType ?? null,
					yearOfBuild: response.data?.ship.yearOfBuild ?? null,
					flag: response.data?.ship.flag ?? "NL",
					bargeType: response.data?.ship.bargeType ?? null,
					classificationSociety: response.data?.ship.classificationSociety ?? "",
					insurer: response.data?.ship.insurer ?? "",
					adnProperties: {
						shipType: response.data?.ship.adnProperties?.shipType ?? null,
						cargoTankDesign: response.data?.ship.adnProperties?.cargoTankDesign ?? null,
						cargoTankType: response.data?.ship.adnProperties?.cargoTankType ?? null,
					},
					tankCladdingType: response.data?.ship.tankCladdingType ?? null,
					capacityTon: response.data?.ship.capacityTon ?? null,
					capacityM3: response.data?.ship.capacityM3 ?? null,
					capacityTeu: response.data?.ship.capacityTeu ?? null,
					tracks: response.data?.ship.tracks ?? null,
					lengthCm:  response.data?.ship.lengthCm ? response.data.ship.lengthCm / 100 : null,
					widthCm:  response.data?.ship.widthCm ? response.data.ship.widthCm / 100 : null,
					minDraughtCm:  response.data?.ship.minDraughtCm ? response.data.ship.minDraughtCm / 100 : null,
					maxDraughtCm:  response.data?.ship.maxDraughtCm ? response.data.ship.maxDraughtCm / 100 : null,
					pumpCapacity: response.data?.ship.pumpCapacity ?? null,
					numberOfTanks: response.data?.ship.numberOfTanks ?? null,
					fuelType: response.data?.ship.fuelType ?? null,
					avgVelocityEmptyKmH: response.data?.ship.avgVelocityEmptyKmH ?? null,
					avgVelocityFullKmH: response.data?.ship.avgVelocityFullKmH ?? null,
					avgFuelConsumptionLtrH: response.data?.ship.avgFuelConsumptionLtrH ?? null,
					avgFuelConsumptionLtrKm: response.data?.ship.avgFuelConsumptionLtrKm ?? null,
					yearLastMaintenance: response.data?.ship.yearLastMaintenance ?? null,
					bobbyCat: response.data?.ship.bobbyCat ?? false,
					sailors: response.data?.ship.sailors ?? null,
					numReeferConnections: response.data?.ship.numReeferConnections ?? null,
					dropline: response.data?.ship.dropline ?? null,
					trussing: response.data?.ship.trussing ?? "",
					hasPushBow: response.data?.ship.hasPushBow ?? false,
					hasPushingCertificate: response.data?.ship.hasPushingCertificate ?? false,
					cellGuides: response.data?.ship.cellGuides ?? false,
					hasShutters: response.data?.ship.hasShutters ?? false,
					shuttersType: response.data?.ship.shuttersType ?? "",
					deckLoad: response.data?.ship.deckLoad ?? null,
					avgSpeedUpstreamKmH: response.data?.ship.avgSpeedUpstreamKmH ?? null,
					avgSpeedDownstreamKmH: response.data?.ship.avgSpeedDownstreamKmH ?? null,
					numberOfCranes: response.data?.ship.numberOfCranes ?? null,
					maxCranesOnQuay: response.data?.ship.maxCranesOnQuay ?? null,
					trackerId: response.data?.ship.trackerId ?? null,
					trackTraceProvider: response.data?.ship.trackTraceProvider ?? null,
					maxTonBallast: response.data?.ship.maxTonBallast ?? null,
					airDraft: response.data?.ship.airDraft ?? null,
					corridor: response.data?.ship.corridor ?? "",
					tankEye: response.data?.ship.tankEye ?? false,
					hasHeatingInstallation: response.data?.ship.hasHeatingInstallation ?? false,
					boiler: response.data?.ship.boiler ?? false,
					spillRail: response.data?.ship.spillRail ?? false,
					selfPropelled: response.data?.ship.selfPropelled ?? false,
					sailingSchedule: response.data?.ship.sailingSchedule?.id ?? null,
					memo: response.data?.ship.memo ?? "",
					planningNotes: response.data?.ship.planningNotes ?? "",

					shipManager: response.data?.ship.shipManager ?? null,
					skipper: response.data?.ship.skipper ?? null,
					shipOwner: response.data?.ship.shipOwner ?? null,
					operator: response.data?.ship.operator ?? null,
					coupledShip: response.data?.ship.coupledShip ?? null,
					defaultCharterSubtype: response.data?.ship.defaultCharterSubtype ?? null,
					retrieveActuals: response.data?.ship.retrieveActuals ?? false,
					showLastProducts: response.data?.ship.showLastProducts ?? false,
					tracking: response.data?.ship.tracking ?? false,
					advancedTracking: response.data?.ship.advancedTracking ?? false,

					tankProperties: {
						tankConfiguration: response.data?.ship?.tankProperties?.tankConfiguration ?? TankConfiguration.CenterTanks,
					},
					tanks: response.data?.ship.tanks?.map(it => ({
						id: it.id ?? null,
						capacityM3: it.capacityM3 ?? null,
						position: it.position,
						positionNumber: it.positionNumber,
					})) ?? [],

					contacts: response.data?.ship.contacts?.map(it => ({
						id: it.id ?? null,
						type: it.type ?? "General",
						name: it.name ?? "",
						insertion: it.insertion ?? "",
						surName: it.surName ?? "",
						email: it.email ?? "",
						telephone: it.telephone ?? "",
						fax: it.fax ?? "",
						address: {
							name1: it.address?.name1 ?? "",
							name2: it.address?.name2 ?? "",
							street: it.address?.street ?? "",
							zipCode: it.address?.zipCode ?? "",
							town: it.address?.town ?? "",
							country: it.address?.country ?? "",
						},
						rhineLicense: it.rhineLicense ?? false,
						grootVaarbewijsA: it.grootVaarbewijsA ?? false,
						grootVaarbewijsB: it.grootVaarbewijsB ?? false,
						radarLicense: it.radarLicense ?? false,
					})) ?? [],

					availability: {
						availableMonday: response.data?.ship.availability?.availableMonday ?? false,
						availableTuesday: response.data?.ship.availability?.availableTuesday ?? false,
						availableWednesday: response.data?.ship.availability?.availableWednesday ?? false,
						availableThursday: response.data?.ship.availability?.availableThursday ?? false,
						availableFriday: response.data?.ship.availability?.availableFriday ?? false,
						availableSaturday: response.data?.ship.availability?.availableSaturday ?? false,
						availableSunday: response.data?.ship.availability?.availableSunday ?? false,

						availableHoursADay: response.data?.ship.availability?.availableHoursADay ?? null,
						availableDayNight: response.data?.ship.availability?.availableDayNight ?? AvailabilityEnum.Day,
						defaultAvailability: response.data?.ship.availability?.defaultAvailability ?? false,
					},

					workingHours: {
						id: response.data?.ship.workingHours?.id ?? null,
						mondayFrom:	response.data?.ship.workingHours?.mondayFrom ?? null,
						mondayTo: response.data?.ship.workingHours?.mondayTo ?? null,
						tuesdayFrom: response.data?.ship.workingHours?.tuesdayFrom ?? null,
						tuesdayTo: response.data?.ship.workingHours?.tuesdayTo ?? null,
						wednesdayFrom: response.data?.ship.workingHours?.wednesdayFrom ?? null,
						wednesdayTo: response.data?.ship.workingHours?.wednesdayTo ?? null,
						thursdayFrom: response.data?.ship.workingHours?.thursdayFrom ?? null,
						thursdayTo: response.data?.ship.workingHours?.thursdayTo ?? null,
						fridayFrom: response.data?.ship.workingHours?.fridayFrom ?? null,
						fridayTo: response.data?.ship.workingHours?.fridayTo ?? null,
						saturdayFrom: response.data?.ship.workingHours?.saturdayFrom ?? null,
						saturdayTo: response.data?.ship.workingHours?.saturdayTo ?? null,
						sundayFrom: response.data?.ship.workingHours?.sundayFrom ?? null,
						sundayTo: response.data?.ship.workingHours?.sundayTo ?? null,
					},

					tonnageCertificates: response.data?.ship.tonnageCertificates ?? defaultTonnageCertificates,
					agents: response.data?.ship?.agents ?? [],

					bayPlan: response.data?.ship.bayPlan ? JSON.parse(response.data?.ship.bayPlan) : null,
				});
			});
		}
	}, []);

	const bayPlan = watch("bayPlan");
	const bargeType = watch("bargeType");

	return (
		<React.Fragment>
			<FormProvider {...form}>
				<form onSubmit={handleSubmit(save)}>
					<PageHeader>
						<PageHeader.Title>
							{Language("SHIP")}
							<Helmet title={intl.formatMessage({ id: "SHIP" })} />
						</PageHeader.Title>

						<PageHeader.Actions>
							<Button onClick={() => navigate(id ? `/view/ships/${id}/details` : "/view/ships", { state: { tab: selectedTab } })} variant="outlined">
								{Language("CANCEL")}
							</Button>

							<Button loading={loading} type="submit">
								{Language("SAVE")}
							</Button>

							<ExternalMasterdataWarningIcon entity={MasterdataEntity.Ship} />
						</PageHeader.Actions>
					</PageHeader>

					<ExternalMasterdataAlert
						entity={MasterdataEntity.Ship}
						externalId={watch("externalId")} />

					<Grid container spacing={3}>
						<Grid size={{ xs: 12, md: 5 }}>
							<Card title={Language("DETAILS")} icon={<Info />}>
								<ShipDetailsForm />
							</Card>
						</Grid>

						<Grid size={{ xs: 12, md: 7 }}>
							<Card disablePadding>
								<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
									<Tabs
										variant='scrollable'
										value={selectedTab}
										onChange={(_event, newValue) => selectTab(newValue)}>
										<Tab label={Language("GENERAL")} value={ShipTabs.General} />

										{bargeType !== BargeType.Tanker
											&& hasOptionalFieldPermission(session, Fields.SHIP, "holds") && (
											<Tab label={Language("HOLDS")} value={ShipTabs.Holds} />
										)}

										{bargeType === BargeType.Tanker
											&& hasOptionalFieldPermission(session, Fields.SHIP, "tanks")
											&& (
												<Tab label={Language("TANKS")} value={ShipTabs.Tanks} />
											)
										}

										{/*TODO: Reimplement in the future*/}
										{/*{hasOptionalFieldPermission(session, Fields.SHIP, "layerCapacities") && (*/}
										{/*	<Tab label={Language("SHIPS_LAYER_CAPACITIES")} value={TABS.LAYER_CAPACITIES} />*/}
										{/*)}*/}

										{hasOptionalFieldPermission(session, Fields.SHIP, "contacts") && (
											<Tab
												label={Language("CONTACTS")}
												value={ShipTabs.Contacts}
												sx={{ color: hasErrorInContactsTab() ? (theme) => theme.palette.error.main : undefined }} />
										)}

										{hasPluginPermission(session, "shipAvailability") && (
											<Tab label={Language("SHIP_AVAILABILITY")} value={ShipTabs.Availability} />
										)}

										{hasPluginPermission(session, "tonnageCertificates") && (
											<Tab label={Language("SHIP_TONNAGE_CERTIFICATES")} value={ShipTabs.TonnageCertificates} />
										)}

										{hasPluginPermission(session, "agents") && (
											<Tab
												label={Language("AGENCIES")}
												value={ShipTabs.Agents}
												sx={{ color: hasErrorInAgentsTab() ? (theme) => theme.palette.error.main : undefined }} />
										)}
									</Tabs>
								</Box>

								{selectedTab === ShipTabs.General && (
									<Box p={2}>
										<ShipGeneralForm />
									</Box>
								)}

								{selectedTab === ShipTabs.Holds && (
									<ShipHoldsForm
										openSelectBayPlanTemplateDialog={() => openSelectBayPlanTemplateDialog(true)}
										openCreateBayPlanTemplateDialog={() => openCreateBayPlanTemplateDialog(true)}
										openShipHoldDialog={() => openShipHoldDialog(true)} />
								)}

								{selectedTab == ShipTabs.Tanks && (
									<ShipTanksForm
										ship={currentShip}
										loading={shipLoading}
									/>
								)}

								{/*TODO: Reimplement in the future*/}
								{/*{selectedTab === TABS.LAYER_CAPACITIES && (*/}
								{/*	<LayerCapacities control={control} />*/}
								{/*)}*/}

								{selectedTab === ShipTabs.Contacts && (
									<ShipContactsForm />
								)}

								{selectedTab === ShipTabs.Availability && (
									<Box p={2}>
										<ShipAvailabilityForm />
									</Box>
								)}

								{selectedTab === ShipTabs.TonnageCertificates && (
									<Box p={2}>
										<ShipTonnageCertificatesForm />
									</Box>
								)}

								{selectedTab === ShipTabs.Agents && (
									<Box>
										<ShipAgentsForm />
									</Box>
								)}
							</Card>
						</Grid>
					</Grid>
				</form>
			</FormProvider>

			<CreateShipHoldDialog
				open={shipHoldDialogOpen}
				onClose={() => openShipHoldDialog(false)}
				refetch={(data => {
					setValue("bayPlan.rows", Number(data.rows));
					setValue("bayPlan.bays", Number(data.bays));
					setValue("bayPlan.tiers", Number(data.tiers));
					setValue("bayPlan.type", BargeType.ContainerShip);
					setValue("bayPlan.properties", []);
				})} />

			{bayPlan && (
				<React.Fragment>
					<SelectBayPlanTemplateDialog
						open={selectBayPlanTemplateDialogOpen}
						onClose={() => openSelectBayPlanTemplateDialog(false)}
						onChange={(template) => {
							const templateBayPlan = JSON.parse(template.bayPlan);
							setValue("bayPlan.properties", templateBayPlan.properties);
						}}
						bayPlan={bayPlan} />

					<CreateBayPlanTemplateDialog
						open={createBayPlanTemplateDialogOpen}
						onClose={() => openCreateBayPlanTemplateDialog(false)}
						bayPlan={bayPlan} />
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

const startDepth = 0.8;
const endDepth = 4;
const interval = 0.1;

const defaultTonnageCertificates = Array.from(Array(((endDepth - startDepth) / interval) + 1).keys()).map((index) => ({
	depth: Number((startDepth + (index * interval)).toFixed(1)),
	tonnage: null,
}));

export default ShipCreateEdit;
